/**
 * pages/account/AccountPage.tsx — UPDATED
 *
 * Changes:
 * - Added KYC status card with clear call-to-action
 * - Payment Methods link now in AccountOptions (see options_changes.ts)
 * - Credit profile moved below KYC
 */

import {
  CheckmarkCircleOutline,
  ShieldCheckmark,
  AlertCircleOutline,
  ChevronForward,
  DocumentTextOutline,
} from "@raresail/react-ionicons";
import { Avatar, Row } from "antd";
import styled from "styled-components";
import { alpha } from "@mui/material";
import { ProfileIcon } from "../../../assets/svg/Svgs";
import {
  primary10,
  primary5,
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
} from "../../../constants/colors";
import { getDisplayName } from "../../../utils/helpers";
import { useAuth } from "../../context/AuthContext";
import { resolveMediaUrl } from "../../../constants/config";
import {
  AnimateChildren,
  Divider,
  Heading,
  SmallTag,
} from "../../elements/Elements";
import { AccountOptions } from "../../../constants/options";
import { MenuItem } from "../../../utils/menuUtils";
import { CreditProfileCard } from "./CreditProfileCard";
import { EmailVerificationCard } from "./EmailVerificationCard";
import { TransactionPinCard } from "./TransactionPinCard";
import { InstitutionCard } from "./InstitutionCard";
import { DeleteAccountCard } from "./DeleteAccountCard";
import { IsBorrower, IsOnboared, kycStatus } from "../../../utils/permissionUtils";
import { Link } from "react-router-dom";
import { ONBOARD } from "../../../constants/constants";

/* ─── Account Card ─────────────────────────────────────────────── */

const AccountCard = () => {
  const { user } = useAuth();
  const { first_name, email, user_type, image } = (user || {}) as any;
  const photoSrc = image ? resolveMediaUrl(image) : undefined;
  return (
    <div className="p15 whiteBg r10 flexColumn gap10 hidden">
      <div className="flexNullCenter gap15">
        <Avatar
          size="large"
          src={photoSrc}
          icon={!photoSrc ? <ProfileIcon /> : undefined}
          style={{
            backgroundColor: photoSrc ? "transparent" : primary10,
            color: PRIMARY_COLOR,
          }}
        >
          {!photoSrc && <div className="semiBold font16">{first_name?.[0]}</div>}
        </Avatar>

        <div className="flexGrow">
          <Row wrap>
            <Heading>{getDisplayName(user)}</Heading>
            <SmallTag icon={ShieldCheckmark}>{user_type}</SmallTag>
          </Row>
          <div className="grey font12">{email}</div>
        </div>
      </div>
    </div>
  );
};

/* ─── KYC Status Card ──────────────────────────────────────────── */

const KYCStatusCard = () => {
  const { user } = useAuth();
  const status = kycStatus(user);
  // "verified" + "pending_review" → all docs submitted, success-style card.
  // "incomplete"  + "missing"        → action-needed warning card.
  const submitted = status === "verified" || status === "pending_review";

  const copy = (() => {
    if (status === "verified") {
      return {
        title: "KYC Verified",
        body: "Your identity has been verified. All features are active.",
      };
    }
    if (status === "pending_review") {
      return {
        title: "KYC Under Review",
        body: "Documents submitted — an admin is reviewing your KYC. You can already apply for loans and invest.",
      };
    }
    if (status === "incomplete") {
      return {
        title: "Finish Your KYC",
        body: "Some KYC details are missing. Upload both sides of your ID, NIN, and date of birth.",
      };
    }
    return {
      title: "Complete Your KYC",
      body: "Verify your identity to access loans, investments, and withdrawals.",
    };
  })();

  return (
    <Link to={`/${ONBOARD}`}>
      <KYCCard completed={submitted} className="pointer animate">
        <div className="flexNullCenter gap12">
          <KYCIcon completed={submitted}>
            {submitted ? (
              <CheckmarkCircleOutline fontSize={22} />
            ) : (
              <AlertCircleOutline fontSize={22} />
            )}
          </KYCIcon>

          <div className="flexGrow">
            <div className="font14 semiBold" style={{ color: submitted ? "#1e293b" : "#92400e" }}>
              {copy.title}
            </div>
            <div className="font12" style={{ color: submitted ? "#64748b" : "#b45309", lineHeight: 1.4, marginTop: 2 }}>
              {copy.body}
            </div>
          </div>

          <ChevronForward
            fontSize={18}
            color={submitted ? "#94a3b8" : "#92400e"}
          />
        </div>

        {!submitted && (
          <KYCProgress>
            <KYCProgressBar />
            <div className="font11" style={{ color: "#92400e", marginTop: 6 }}>
              Estimated time: 2 minutes
            </div>
          </KYCProgress>
        )}
      </KYCCard>
    </Link>
  );
};

/* ─── Main Page ─────────────────────────────────────────────────── */

export const AccountPage = () => {
  return (
    <div className="p15 flexColumn gap15">
      <AnimateChildren>
        <AccountCard />

        {/* KYC Status — always visible with clear CTA */}
        <KYCStatusCard />

        {/* Email verification — hides itself once verified */}
        <EmailVerificationCard />

        {/* Transaction PIN — set / change / forgot */}
        <TransactionPinCard />

        {/* Institution (business) profile + KYC documents */}
        <InstitutionCard />

        {/* KYC ID upload moved into onboarding (single entry point).
            The KYCStatusCard above links to /onboard which now hosts
            the full KYC form including ID front + back. */}

        {/* Credit Profile — only for borrowers */}
        <CreditProfileCard />

        {/* Account Menu */}
        <div className="p15 whiteBg r10 flexColumn gap10">
          <Heading>My Account</Heading>
          <Divider />

          <div className="flexColumn gap5">
            <AnimateChildren>
              {AccountOptions().map((v, i) => (
                <MenuItem key={i} data={v} />
              ))}
            </AnimateChildren>
          </div>
        </div>

        {/* Danger zone — account deletion request (30-day grace window).
            Always visible (even on slow API responses) so users have a
            clear entry point. */}
        <div className="flexColumn gap5">
          <Heading style={{ color: "#b91c1c" }}>Danger Zone</Heading>
          <DeleteAccountCard />
        </div>
      </AnimateChildren>
    </div>
  );
};

/* ─── Styles ────────────────────────────────────────────────────── */

const KYCCard = styled.div<{ completed?: boolean }>`
  padding: 16px;
  border-radius: 12px;
  background: ${({ completed }) =>
    completed ? "#f8fafc" : "#fffbeb"};
  border: 1.5px solid
    ${({ completed }) =>
      completed ? PRIMARY_LIGHT : "#fde68a"};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ completed }) =>
      completed
        ? alpha(PRIMARY_COLOR, 0.2)
        : "#fbbf24"};
    box-shadow: 0 2px 8px
      ${({ completed }) =>
        completed
          ? alpha(PRIMARY_COLOR, 0.04)
          : "rgba(251, 191, 36, 0.08)"};
  }
`;

const KYCIcon = styled.div<{ completed?: boolean }>`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ completed }) => (completed ? "#fff" : "#92400e")};
  background: ${({ completed }) =>
    completed ? "#10b981" : "#fef3c7"};
`;

const KYCProgress = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #fde68a;
`;

const KYCProgressBar = styled.div`
  height: 4px;
  border-radius: 2px;
  background: #fde68a;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 30%;
    background: #f59e0b;
    border-radius: 2px;
  }
`;